import { Navigate, Outlet, useLocation } from "react-router-dom"
import { useEffect } from "react"
import { useAuth as useClerkAuth, useClerk } from "@clerk/react"
import { useCurrentUser } from "@/features/auth/hooks/useCurrentUser"
import { requireLogin } from "@/lib/authGate"
import { Loader2 } from "lucide-react"

function Spinner() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  )
}

/** Signed out: home, with the sign-in dialog open, coming back here afterwards. */
function SignInFirst() {
  const { pathname, search } = useLocation()
  const back = `${pathname}${search}`
  useEffect(() => {
    requireLogin("login_required", back === "/ask" ? null : back)
  }, [back])
  return <Navigate to="/ask" replace />
}

export function RequireAuth() {
  const { isLoaded, isSignedIn } = useClerkAuth()
  const { me, isLoading, error, refetch } = useCurrentUser()
  const { signOut } = useClerk()

  if (!isLoaded) return <Spinner />
  if (!isSignedIn) return <SignInFirst />
  if (error) {
    const status = (error as Error & { status?: number }).status
    // A 401 here comes after a freshly refreshed token, so Clerk says signed in and the API says
    // no (an origin the API does not accept, a deleted account). Signing out and sending the reader
    // to sign in again only repeats it, so say so and let them choose.
    const message =
      status === 401
        ? "You are signed in, but NitiAssist could not confirm your session."
        : status === 403
          ? "Your account does not have access to this organization."
          : status === 503
            ? "The service is temporarily unavailable."
            : "We could not load your account."
    return (
      <div role="alert" className="flex flex-col items-center justify-center min-h-screen gap-3 bg-background">
        <p>{message}</p>
        <div className="flex gap-2">
          <button type="button" className="btn" onClick={() => refetch()}>
            Retry
          </button>
          {status === 401 ? (
            <button type="button" className="btn btn--ghost" onClick={() => void signOut({ redirectUrl: "/" })}>
              Sign out
            </button>
          ) : null}
        </div>
      </div>
    )
  }
  if (isLoading || !me) return <Spinner />

  return <Outlet />
}
